import { Div, createEmitter } from '@codesuma/baseline'
import colors from '../../../configs/colors'
import theme from '../../../services/theme'
import helpers from '../../../helpers'
import style from './index.module.css'

// Bottom-sheet color picker: one swatch per palette hue, rendered from the
// active theme's palette in color-wheel order (neutrals first). The sheet wears
// the app surface color — the same backdrop the home circles sit on — so a
// swatch previews exactly how the list will look on the grid. Emits 'pick'
// { index } and 'close'. Overlay conventions: body-level, safe-area bottom
// padding, back closes it via a state-only history marker (see share-panel).
export const ColorPanel = () => {
    const emitter = createEmitter()

    const overlay = Div()
    overlay.addClass(style.overlay)

    const panel = Div()
    panel.addClass(style.panel)

    const title = Div('List color')
    title.addClass(style.title)

    const grid = Div()
    grid.addClass(style.grid)

    panel.append(title, grid)
    overlay.append(panel)

    // Back = close (same marker pattern as the share panel).
    let markerLive = false
    const onPop = () => {
        window.removeEventListener('popstate', onPop)
        markerLive = false
        emitter.emit('close')
    }
    const requestClose = () => {
        if (markerLive) {
            markerLive = false
            history.back() // onPop delivers the actual 'close'
        } else {
            emitter.emit('close')
        }
    }
    overlay.el.onclick = (e: MouseEvent) => {
        if (e.target === overlay.el) requestClose()
    }

    // Display order: neutrals first, then hue ascending around the color wheel.
    // Computed from the light palette (the palettes are index-aligned, so hue
    // order is theme-invariant). Only the *display* is sorted — the emitted
    // index is the palette index, so stored picks don't depend on this order.
    const hueOf = (hex: string): { h: number; s: number } => {
        const r = parseInt(hex.slice(1, 3), 16) / 255
        const g = parseInt(hex.slice(3, 5), 16) / 255
        const b = parseInt(hex.slice(5, 7), 16) / 255
        const max = Math.max(r, g, b)
        const d = max - Math.min(r, g, b)
        let h = 0
        if (d) {
            if (max === r) h = 60 * (((g - b) / d + 6) % 6)
            else if (max === g) h = 60 * ((b - r) / d + 2)
            else h = 60 * ((r - g) / d + 4)
        }
        return { h, s: max === 0 ? 0 : d / max }
    }
    const displayOrder = colors.LIST_PALETTE
        .map((entry, index) => ({ index, ...hueOf(entry.bg) }))
        .sort((a, b) => (a.s < 0.05 ? -1 : a.h) - (b.s < 0.05 ? -1 : b.h))
        .map(x => x.index)

    const show = (listId: string) => {
        // Swatches are rebuilt fresh every show (baseline empty() is permanent
        // for reused children, so no instance is ever reused across shows).
        const palette = theme.get() === 'dark' ? colors.LIST_PALETTE_DARK : colors.LIST_PALETTE
        const current = helpers.paletteIndexForId(listId)
        grid.empty()
        displayOrder.forEach(index => {
            const entry = palette[index]
            const swatch = Div()
            swatch.addClass(style.swatch)
            if (index === current) swatch.addClass(style.current)
            // color carries the hue's own text color so the "current" ring
            // (currentColor) stays legible on either sheet background.
            swatch.style({ backgroundColor: entry.bg, color: entry.text })
            swatch.el.onclick = () => {
                emitter.emit('pick', { index })
                requestClose()
            }
            grid.append(swatch)
        })

        if (!markerLive) {
            history.pushState({ colorPanel: true }, '')
            markerLive = true
            window.addEventListener('popstate', onPop)
        }
    }

    return Object.assign(overlay, {
        on: emitter.on.bind(emitter),
        show,
    })
}
